import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { PlusIcon, TrashIcon, CalendarSearch } from 'lucide-react';

import {
  convertTo24HourFormat,
  convertTo12HourFormat
} from '@/helpers/dateHelpers';

import { Card, CardContent } from '@/components/ui/card';

import SelectDate from '@/components/form/SelectDate';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription
} from '@/components/ui/empty';

import SelectTime from '@/components/form/SelectTime';
import { DAY_OF_WEEK } from '../../enums';
import SelectWeekday from './SelectWeekday';

import { useOffering } from '@/data/offering';

const FormGeneral = ({
  setFormError,
  timeSlotErrorMessage,
  setTimeSlotErrorMessage
}) => {
  const params = useParams();
  const offering = useOffering(params.offeringId);
  const [selectedDay, setSelectedDay] = useState(0);
  const [selectedTime, setSelectedTime] = useState('');
  const form = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'offeringSessionRules'
  });

  const offeringLength = offering.data.length; // Length in minutes

  // Function to check for overlaps
  const checkForOverlaps = (time) => {
    const newStartTime = new Date();
    const [newHours, newMinutes] = time.split(':');
    newStartTime.setHours(newHours, newMinutes);

    // Calculate the end time based on the offering length
    const newEndTime = new Date(
      newStartTime.getTime() + offeringLength * 60000
    );

    return fields.some((field) => {
      // Check if the existing field is for the same day
      if (field.dayOfWeek !== selectedDay) {
        return false; // Skip this field if it's not the same day
      }

      const existingStartTime = new Date();
      const [existingHours, existingMinutes] = field.time.split(':');
      existingStartTime.setHours(existingHours, existingMinutes);

      const existingEndTime = new Date(
        existingStartTime.getTime() + offeringLength * 60000
      );

      // Allow adding if the new time starts when the existing time ends or vice versa
      return (
        newStartTime < existingEndTime && newEndTime > existingStartTime // Overlap
      );
    });
  };

  // Determine the time slot error message based on the button state
  const getTimeSlotErrorMessage = () => {
    if (!selectedTime) {
      return 'Please select a time.';
    }

    // Check if the selected time already exists for the selected day
    const isTimeAlreadyAdded = fields.some(
      (field) => field.dayOfWeek === selectedDay && field.time === selectedTime
    );
    if (isTimeAlreadyAdded) {
      return 'This time slot has already been added.';
    }
    if (checkForOverlaps(selectedTime)) {
      return `This time overlaps with an existing slot on the same day. ${offering.data.name || 'This offering'} has a length of ${offeringLength} minutes.`;
    }
    return '';
  };

  const handleAddTime = () => {
    const timeSlotErrorMessage = getTimeSlotErrorMessage();
    setTimeSlotErrorMessage(timeSlotErrorMessage);
    if (timeSlotErrorMessage) {
      return;
    }
    append({
      dayOfWeek: selectedDay,
      time: selectedTime
    });
    setFormError({});
  };

  const handleRemoveTime = (id) => {
    const index = fields.findIndex((field) => field.id === id);
    remove(index);
    setTimeSlotErrorMessage('');
    setFormError({});
  };

  const handleSelectTime = (time) => {
    setSelectedTime(convertTo24HourFormat(time));
    setTimeSlotErrorMessage('');
    setFormError({});
  };

  const handleSelectDay = (day) => {
    setSelectedDay(day);
    setTimeSlotErrorMessage('');
    setFormError({});
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <FormField
          control={form.control}
          name="startDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Start Date</FormLabel>
              <FormControl>
                <SelectDate value={field.value} onChange={field.onChange} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="endDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>End Date</FormLabel>
              <FormControl>
                <SelectDate value={field.value} onChange={field.onChange} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <span className="text-sm mb-2 block">Select Weekdays</span>
      <SelectWeekday
        selectedDay={selectedDay}
        highlightedDays={fields
          .map((field) => field.dayOfWeek)
          .filter(
            (value, index, self) =>
              self.indexOf(value) === index &&
              fields.some((f) => f.dayOfWeek === value)
          )}
        onClickDay={handleSelectDay}
      />

      <span className="text-sm mb-2 block">
        Add {DAY_OF_WEEK[selectedDay]} Time
      </span>
      <div className="flex justify-between">
        <SelectTime onChange={handleSelectTime} />
        <Button
          variant="default"
          size="icon"
          onClick={handleAddTime}
          type="button"
        >
          <PlusIcon className="w-4 h-4" />
        </Button>
      </div>
      {timeSlotErrorMessage && (
        <p className="text-destructive text-sm mt-2">{timeSlotErrorMessage}</p>
      )}
      <Separator className="my-4" />
      <div className="mb-4">
        <span className="text-sm mb-4 block">
          {DAY_OF_WEEK[selectedDay]} Time Slots
        </span>
        <div className="space-y-2">
          {fields.filter((field) => field.dayOfWeek === selectedDay).length ===
          0 ? (
            <Empty>
              <EmptyIcon>
                <CalendarSearch />
              </EmptyIcon>
              <EmptyTitle>No {DAY_OF_WEEK[selectedDay]} Time Slots</EmptyTitle>
              <EmptyDescription>Try adding a time slot</EmptyDescription>
            </Empty>
          ) : (
            fields
              .filter((field) => field.dayOfWeek === selectedDay)
              .sort((a, b) => a.time.localeCompare(b.time))
              .map((field) => (
                <Card key={field.id} className="bg-muted">
                  <CardContent className="flex justify-between p-0">
                    <div className="flex items-center gap-2 px-4">
                      <span>{DAY_OF_WEEK[field.dayOfWeek]} at</span>
                      <span>{convertTo12HourFormat(field.time)}</span>
                    </div>
                    <Button
                      variant="icon"
                      size="icon"
                      onClick={() => handleRemoveTime(field.id)}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))
          )}
        </div>
      </div>
    </>
  );
};

export default FormGeneral;
